import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { mainSiteUrl, categories } from '@/mocks/categories';

const languages = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const currentLang = (i18n.language || 'en').split('-')[0];
  const current = languages.find((l) => l.code === currentLang) || languages[0];

  const changeLang = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
    setMobileOpen(false);
  };

  const routeLinks = [
    { to: '/', label: t('nav.home'), icon: 'ri-home-4-line' },
    { to: '/article/usfans-buying-guide', label: t('nav.shoppingGuide'), icon: 'ri-shopping-bag-3-line' },
  ];

  const anchorLinks = [
    { href: '#categories', label: t('nav.categories') },
    { href: '#guides', label: t('nav.guides') },
    { href: '#reviews', label: t('nav.reviews') },
    { href: '#safety', label: t('nav.safety') },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-background-200 bg-background-50">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-6">
        <Link to="/" className="flex items-center gap-2" aria-label={t('brand')}>
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-table-line text-lg" />
          </span>
          <span className="text-base font-bold text-foreground-950 whitespace-nowrap">
            {t('brand')}
          </span>
        </Link>

        <div className="hidden items-center gap-6 lg:flex">
          {routeLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="flex items-center gap-1.5 text-sm font-semibold text-foreground-800 transition-colors hover:text-primary-600 whitespace-nowrap"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className={`${link.icon} text-sm`} />
              </span>
              {link.label}
            </Link>
          ))}
          {anchorLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-sm font-medium text-foreground-700 transition-colors hover:text-primary-600 whitespace-nowrap"
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <button
              type="button"
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 whitespace-nowrap cursor-pointer"
            >
              <span>{current.flag}</span>
              <span className="hidden sm:inline">{current.label}</span>
              <i className={`ri-arrow-down-s-line text-sm transition-transform ${langOpen ? 'rotate-180' : ''}`} />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-40 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => changeLang(lang.code)}
                    className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-background-100 cursor-pointer ${
                      lang.code === currentLang ? 'font-semibold text-primary-700' : 'text-foreground-700'
                    }`}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="hidden items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap md:flex"
          >
            <i className="ri-shopping-cart-line text-sm" />
            {t('nav.cta')}
          </a>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-900 lg:hidden cursor-pointer"
            aria-label="Menu"
          >
            <i className={`text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-5 lg:hidden">
          <div className="flex flex-col gap-1">
            {routeLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:bg-background-100"
              >
                <span className="flex h-6 w-6 items-center justify-center text-primary-600">
                  <i className={`${link.icon} text-base`} />
                </span>
                {link.label}
              </Link>
            ))}
            {anchorLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100"
              >
                <span className="flex h-6 w-6 items-center justify-center text-foreground-500">
                  <i className="ri-arrow-right-s-line text-base" />
                </span>
                {link.label}
              </a>
            ))}
          </div>

          <div className="mt-5 border-t border-background-200 pt-5">
            <p className="px-1 text-xs font-semibold uppercase tracking-wider text-foreground-500">
              {t('category.browse')}
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 hover:border-primary-400"
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-background-100 text-primary-600">
                    <i className={`${cat.icon} text-sm`} />
                  </span>
                  {t(cat.nameKey)}
                </a>
              ))}
            </div>
          </div>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="mt-5 flex items-center justify-center gap-1.5 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50"
          >
            <i className="ri-shopping-cart-line text-sm" />
            {t('nav.cta')}
          </a>
        </div>
      )}
    </header>
  );
}